import { useCallback } from 'react';
import { supabase } from '../../supabase';
import { tenantToday } from '../../utils/date/tenantDate';
import { setCustomerLifecycleStatus } from '../../utils/customer/customerLifecycle';
import {
  findCustomerNameInMessages,
  buildLineItemsFromPreFill,
} from './useAiChatMessageAppend';
import { aiSafeActionError } from '../../utils/error/aiErrorMessages';
import { withRetry } from '../../utils/ai/withRetry';
import { useLifecycleActivation } from '../../ui/lifecycleActivation/LifecycleActivationContext';
import { getFieldReadTables } from '../../utils/job/fieldReadTables';
import { isAutoStagingTransition } from '../../utils/customer/lifecycleTransitionToast';
import { requestCustomerOpen } from '../../utils/ui/pendingCustomerOpen';
import { openRecordOnTop } from '../../utils/ui/recordOpeners';

// KAN-843: only these roles have a Customers screen to open.
const CUSTOMER_SCREEN_ROLES = ['admin', 'operations'];

// Bug 10/11/12: verify customer belongs to current tenant via RLS-protected SELECT
//
// RD-ROLE-005b: a blind field role has no base `customers` SELECT — this is a
// plain module function called from action handlers below (not a component/
// hook render), so it reads the store-backed getFieldReadTables() rather
// than usePriceVisibility() (which would drag AuthContext -> react-intl into
// every consumer's test, the useDuplicateServiceGuard lesson).
//
// Also returns `lifecycleStatus` (lifecycle-truth, 2026-09-16) so
// confirmLifecycleAction can decide whether a transition ACTUALLY auto-stages
// an invoice (opening→active / closing→closed) instead of guessing from
// `new_status` alone — reading it here costs no extra round trip.
async function verifyCustomerInTenant(customerId) {
  if (!customerId) return { ok: false, lifecycleStatus: null };
  const fieldTables = getFieldReadTables();
  const { data, error } = await supabase
    .from(fieldTables.customers)
    .select('id, lifecycle_status')
    .eq('id', customerId)
    .maybeSingle();
  if (error || !data) return { ok: false, lifecycleStatus: null };
  return { ok: true, lifecycleStatus: data.lifecycle_status || null };
}

export default function useAiAssistantActions({
  messages,
  messagesRef,
  setMessages,
  setPanelOpen,
  onNavigate,
  role,
  tz,
  locale,
  onChildActionCompleted,
  onChildActionDismissed,
  confirmSendSummaryActionInner,
  confirmBulkSendSummaryAction,
  sendSummaryInline,
}) {
  const { beginActivation } = useLifecycleActivation();

  const executeAction = useCallback(async (action) => {
    // KAN-1186: the customer opens ON TOP of the current page (the panel closes at the end of this
    // action); the tab switch + pending-open hand-off is only the fallback for a home with no opener.
    const openCustomerScreen = async (customerId) => {
      if (!onNavigate || !CUSTOMER_SCREEN_ROLES.includes(role)) return; // no customer screen for this role
      if (await openRecordOnTop('customer', customerId)) return;
      requestCustomerOpen(customerId);
      onNavigate('/app/customers');
    };
    if (!action || action.type === 'none') return;
    if (action.type === 'next_stop_bar') return;
    if (action.type === 'customer_list' || action.type === 'quick_log_compose') return;

    if (action.type === 'navigate' && onNavigate) {
      if (action.customer_id) {
        // Bug 10: verify customer belongs to tenant before navigating
        const { ok } = await verifyCustomerInTenant(action.customer_id);
        if (!ok) { console.warn('[AI] customer not found in tenant, navigation blocked'); return; }
        await openCustomerScreen(action.customer_id);
      } else if (action.job_id) {
        if (!(await openRecordOnTop('job', action.job_id))) onNavigate(`/jobs/${action.job_id}`);
      }
    }

    if (action.type === 'schedule_follow_up' && action.customer_id && onNavigate) {
      // Bug 10: verify before navigating
      const { ok } = await verifyCustomerInTenant(action.customer_id);
      if (!ok) { console.warn('[AI] customer not found in tenant, navigation blocked'); return; }
      await openCustomerScreen(action.customer_id);
    }

    if (action.type === 'open_log_job' && action.customer_id && onNavigate) {
      // Bug 10: verify before navigating
      const { ok } = await verifyCustomerInTenant(action.customer_id);
      if (!ok) { console.warn('[AI] customer not found in tenant, navigation blocked'); return; }
      // A3: removed pre-navigation 'cancelled' status — card stays 'pending' during nav; no flash
      onNavigate(`/log-job/${action.customer_id}`, {
        state: { pre_fill: action.pre_fill, returnTo: window.location.pathname },
      });
    }

    setPanelOpen(false);
  }, [onNavigate, setPanelOpen, role]);

  const approveAction = useCallback(async (messageId) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg?.action?.pre_fill || msg.actionStatus !== 'pending') return;

    // Bug 11: verify customer in tenant before RPC call
    const { ok } = await verifyCustomerInTenant(msg.action.customer_id);
    if (!ok) {
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, actionStatus: 'failed', actionResult: { error: 'CUSTOMER_NOT_FOUND' } }
          : m
      ));
      return;
    }

    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, actionStatus: 'processing' } : m
    ));

    try {
      const pf = msg.action.pre_fill;
      const lineItems = buildLineItemsFromPreFill(pf);
      const { data, error } = await withRetry(() => supabase.rpc('create_job_from_ai', {
        p_customer_id: msg.action.customer_id,
        p_date: tenantToday(tz),
        p_line_items: lineItems,
        p_notes: pf.notes || '',
        p_idempotency_key: msg.action.idempotency_key || null,
      }));
      if (error) throw error;

      // Bug 9: re-check message still exists after await
      if (!messagesRef.current.find(m => m.id === messageId)) return;

      // Pre-fetch the customer's comm fields so the inline "Enviar resumen"
      // tap on the Trabajo Registrado card skips the jobs-table lookup
      let commFields = {};
      try {
        const fieldTables = getFieldReadTables();
        const { data: custRow } = await supabase
          .from(fieldTables.customers)
          .select('email, comm_opt_out, email_summaries_opt_out')
          .eq('id', msg.action.customer_id)
          .maybeSingle();
        if (custRow) {
          commFields = {
            email: custRow.email || '',
            comm_opt_out: Boolean(custRow.comm_opt_out),
            opted_out: Boolean(custRow.comm_opt_out || custRow.email_summaries_opt_out),
          };
        }
      } catch { /* best-effort; inline send will fall back to Path B */ }

      // Bug 9: re-check after second await
      if (!messagesRef.current.find(m => m.id === messageId)) return;

      const jobPreview = {
        job_id: data.job_id,
        customer_id: msg.action.customer_id,
        customer_name: findCustomerNameInMessages(messages, msg.action.customer_id) || msg.action.customer_name || '',
        service_date: tenantToday(tz),
        ...commFields,
      };

      setMessages((prev) => prev.map((m) =>
        m.id === messageId
          ? { ...m, actionStatus: 'completed', actionResult: data, _jobPreviewForSummary: jobPreview }
          : m,
      ));

      let timedOut = false;
      const summaryPromise = supabase.functions.invoke('ai-generate-job-summary', {
        body: { job_id: data.job_id },
      });
      const timeoutPromise = new Promise(resolve => setTimeout(() => { timedOut = true; resolve({ data: null }); }, 10000));
      const summaryResult = await Promise.race([summaryPromise, timeoutPromise]);
      const insight = summaryResult?.data?.insight || null;

      // Bug 9: re-check after summary await
      if (!messagesRef.current.find(m => m.id === messageId)) return;

      if (insight) {
        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, insight } : m
        ));
      } else if (timedOut) {
        // A5: flag timeout so callers can distinguish from a real null insight
        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, _summaryTimedOut: true } : m
        ));
      }

      onChildActionCompleted(messageId);
    } catch (err) {
      // Bug 9: only write if message still exists
      if (!messagesRef.current.find(m => m.id === messageId)) return;
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, actionStatus: 'failed', actionResult: { error: aiSafeActionError(err) } }
          : m
      ));
    }
  }, [messages, messagesRef, tz, onChildActionCompleted, setMessages]);

  /** @param {'generate_and_send'|'generate_only'} [mode] */
  const requestPostJobSummaryFromCta = useCallback((messageId, jobId, mode = 'generate_and_send') => {
    const src = messagesRef.current?.find((m) => m.id === messageId);
    sendSummaryInline(messageId, jobId, src?._jobPreviewForSummary, mode);
  }, [sendSummaryInline, messagesRef]);

  const dismissAction = useCallback((messageId) => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, actionStatus: 'dismissed' } : m
    ));
    // D1: release any customer-list row lock held by this card
    onChildActionDismissed?.(messageId);
  }, [setMessages, onChildActionDismissed]);

  const confirmFollowUpAction = useCallback(async (messageId) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg?.action || msg.actionStatus !== 'pending') return;

    // Bug 12: verify customer in tenant before RPC
    const { ok } = await verifyCustomerInTenant(msg.action.customer_id);
    if (!ok) {
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, actionStatus: 'failed', actionResult: { error: 'CUSTOMER_NOT_FOUND' } }
          : m
      ));
      return;
    }

    // Bug 9: re-check after verify await
    if (!messagesRef.current.find(m => m.id === messageId)) return;

    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, actionStatus: 'processing' } : m
    ));

    try {
      if (msg.action.type === 'resolve_follow_up') {
        const { error } = await supabase.rpc('resolve_customer_follow_up', {
          p_customer_id: msg.action.customer_id,
        });
        if (error) throw error;
      } else if (msg.action.type === 'set_follow_up') {
        const { data: { session } } = await supabase.auth.getSession();
        const payload = {
          note: msg.action.follow_up_note || '',
          locale: String(locale || 'en').slice(0, 5),
          customer_id: msg.action.customer_id,
          flagged_at: new Date().toISOString(),
          flagged_by: session?.user?.id || null,
          source: 'ai_chat',
        };
        const { error } = await supabase.rpc('set_customer_pending_follow_up', {
          p_customer_id: msg.action.customer_id,
          p_payload: payload,
        });
        if (error) throw error;
      }

      // Bug 9: re-check after RPC await
      if (!messagesRef.current.find(m => m.id === messageId)) return;

      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, actionStatus: 'completed' } : m
      ));
      onChildActionCompleted(messageId);
    } catch (err) {
      // Bug 9: only write if message still exists
      if (!messagesRef.current.find(m => m.id === messageId)) return;
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, actionStatus: 'failed', actionResult: { error: aiSafeActionError(err) } }
          : m
      ));
    }
  }, [messages, messagesRef, locale, onChildActionCompleted, setMessages]);

  const confirmSendSummaryAction = useCallback(async (messageId) => {
    // Bug 9: check existence before and after the inner confirm
    if (!messagesRef.current.find(m => m.id === messageId)) return;
    await confirmSendSummaryActionInner(messageId);
    // Bug 9: re-check after await
    if (!messagesRef.current.find(m => m.id === messageId)) return;
    setTimeout(() => {
      const m = messagesRef.current.find((x) => x.id === messageId);
      if (m?.actionStatus === 'completed') onChildActionCompleted(messageId);
    }, 0);
  }, [confirmSendSummaryActionInner, onChildActionCompleted, messagesRef]);

  const confirmLifecycleAction = useCallback(async (messageId) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg?.action || msg.action.type !== 'change_lifecycle' || msg.actionStatus !== 'pending') return;

    // Bug 12: verify customer in tenant before calling setCustomerLifecycleStatus.
    // Also carries the customer's CURRENT lifecycle_status (lifecycle-truth,
    // 2026-09-16) so the staging-review gate below is decided by the REAL
    // from→to pair, not by new_status alone.
    const { ok, lifecycleStatus: currentStatus } = await verifyCustomerInTenant(msg.action.customer_id);
    if (!ok) {
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, actionStatus: 'failed', actionResult: { error: 'CUSTOMER_NOT_FOUND' } }
          : m
      ));
      return;
    }

    // Bug 9: re-check after verify await
    if (!messagesRef.current.find(m => m.id === messageId)) return;

    // The actual transition + chat-status update. Returns false on failure so the
    // activation review gate (below) keeps its summary open for retry.
    const runTransition = async (includeJobIds, dateFromSheet, excludeJobIds) => {
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, actionStatus: 'processing' } : m
      ));
      try {
        const { error } = await setCustomerLifecycleStatus(
          msg.action.customer_id,
          msg.action.new_status,
          dateFromSheet || msg.action.service_start_date || null,
          { includeJobIds, excludeJobIds },
        );
        if (error) throw error;
        // Bug 9: re-check after lifecycle await
        if (!messagesRef.current.find(m => m.id === messageId)) return true;
        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, actionStatus: 'completed' } : m
        ));
        return true;
      } catch (err) {
        // Bug 9: only write if message still exists
        if (!messagesRef.current.find(m => m.id === messageId)) return false;
        setMessages(prev => prev.map(m =>
          m.id === messageId
            ? { ...m, actionStatus: 'failed', actionResult: { error: aiSafeActionError(err) } }
            : m
        ));
        return false;
      }
    };

    // Opening→active / closing→closed stage a lifecycle invoice — review the
    // bundle (with duplicate flags) before the AI's action commits. The chat
    // message stays 'pending' while the modal is up, so a cancel is re-confirmable.
    // Decided from the REAL from→to pair (isAutoStagingTransition), not from
    // new_status alone — inactive→active / active→closed never stage anything,
    // so they must go straight to runTransition() with no review summary.
    const phase = isAutoStagingTransition(currentStatus, msg.action.new_status)
      ? (msg.action.new_status === 'active' ? 'opening' : 'closing')
      : null;
    if (phase) {
      beginActivation({
        customerId: msg.action.customer_id,
        customerName: findCustomerNameInMessages(messages, msg.action.customer_id) || '',
        phase,
        onConfirmed: runTransition,
        customer: {
          id: msg.action.customer_id,
          full_name: findCustomerNameInMessages(messages, msg.action.customer_id) || '',
        },
      });
      return;
    }

    await runTransition();
  }, [messages, messagesRef, setMessages, beginActivation]);

  const confirmEodCloseoutAction = useCallback(async (messageId, jobIds) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg || msg._eodBulkSent) return;
    // Bug 9: no customer_id needed here (EOD bulk is job-level), just guard post-await
    await confirmBulkSendSummaryAction(messageId, jobIds);
    // Bug 9: re-check after await
    if (!messagesRef.current.find(m => m.id === messageId)) return;
  }, [messages, messagesRef, confirmBulkSendSummaryAction]);

  return {
    executeAction,
    approveAction,
    dismissAction,
    confirmFollowUpAction,
    confirmSendSummaryAction,
    confirmLifecycleAction,
    confirmEodCloseoutAction,
    requestPostJobSummaryFromCta,
  };
}
